import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { findRacerProducts } from "@/pages/CollectionPage";
import { formatProductName, shopifyImg, shopifySrcSet, SIZES_GRID_4 } from "@/lib/format";

const JEWELRY_NAMES = ["VVS1 Star Bracelet", "VVS1 Tennis Bracelet", "Tear Bracelet", "Angel Eyes Bracelet"];
const NON_APPAREL = ["MYSTERY BOX", "After Hours Bundle", ...JEWELRY_NAMES];

const HERO_IMAGE = "/hero-desktop.webp";
const MOBILE_HERO_IMAGE = "/hero-mobile.webp";

/* Labeled placeholder slot for photography the owner will upload */
function PlaceholderSlot({ label, className = "", testid, corner = false }) {
  return (
    <div data-testid={testid} className={`relative bg-[#141414] border border-[#8A8A8A]/20 overflow-hidden ${corner ? "" : "flex items-center justify-center"} ${className}`}>
      <div className="absolute inset-3 border border-dashed border-[#8A8A8A]/20" />
      <span className={`relative text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#8A8A8A] ${corner ? "absolute bottom-5 left-5" : ""}`}>{label}</span>
    </div>
  );
}

export default function HomePage() {
  const [products, setProducts] = useState([]);

  const fetchProducts = useCallback(() => {
    api.get("/shopify/products").then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
  }, []);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const racer = findRacerProducts(products);
  const apparel = products.filter((p) => !p.is_bundle && !NON_APPAREL.includes(p.name));

  return (
    <div data-testid="home-page" className="bg-[#0A0A0A] min-h-screen">
      <SEO title="AERWEAR | After Hours Athletics" description="Luxury athletic streetwear. Built for motion." />

      {/* ─── 1. HERO ─────────────────────────────── */}
      <section data-testid="hero-section" className="relative h-[88vh] sm:h-screen w-full overflow-hidden">
        {/* One <picture> so each screen downloads only its own hero (was both, one hidden by CSS) */}
        <picture>
          <source media="(min-width: 640px)" srcSet={HERO_IMAGE} width="1672" height="941" />
          <img src={MOBILE_HERO_IMAGE} width="1080" height="1626" alt="AERWEAR Racer Collection jerseys and track pants, worn beside a car at night." className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" />
        </picture>
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-20 sm:pb-24 text-center px-6">
          <h1 className="font-['Oswald'] text-5xl sm:text-7xl font-bold uppercase tracking-[0.3em] text-[#F2F0EB] drop-shadow-lg">AERWEAR</h1>
          <Link
            to="/collection"
            data-testid="hero-cta-button"
            className="mt-8 inline-block bg-[#F2F0EB] text-[#0A0A0A] px-8 py-3.5 font-['Oswald'] text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors"
          >
            Shop The Racer Collection
          </Link>
        </div>
      </section>

      {/* ─── 2. RACER COLLECTION LOOKBOOK ────────── */}
      {racer.length > 0 && (
        <section data-testid="racer-collection-strip" className="py-16 sm:py-24 px-6 sm:px-12">
          <h2 className="text-xl sm:text-2xl font-medium uppercase tracking-[0.2em] text-[#F2F0EB] mb-10">Racer Collection</h2>
          <div data-testid="racer-grid" className="grid grid-cols-2 gap-x-3 gap-y-8 sm:flex sm:gap-6 sm:overflow-x-auto sm:scrollbar-hide">
            {racer.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} data-testid={`racer-item-${p.id}`} className="group w-full sm:flex-shrink-0 sm:w-[32vw] lg:w-[23vw]">
                <div className="relative aspect-[4/5] bg-[#141414] overflow-hidden border border-[#8A8A8A]/10 mb-4">
                  {p.images?.[0] ? (
                    <img src={shopifyImg(p.images[0], 720)} srcSet={shopifySrcSet(p.images[0])} sizes={SIZES_GRID_4} width="720" height="900" alt={`AERWEAR ${p.name}, lookbook`} className="w-full h-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  ) : null}
                  {p.available === false && (
                    <div data-testid={`racer-sold-out-${p.id}`} className="absolute inset-0 z-10 flex items-center justify-center bg-black/55">
                      <div className="w-full bg-[#0A0A0A]/85 border-y border-[#F2F0EB]/25 py-2 sm:py-2.5">
                        <span className="block text-center font-['Oswald'] text-sm sm:text-lg font-bold uppercase tracking-[0.3em] text-[#F2F0EB]">Sold Out</span>
                      </div>
                    </div>
                  )}
                </div>
                <h3 className="font-['Oswald'] text-xs sm:text-sm font-medium uppercase tracking-[0.15em] text-[#F2F0EB] truncate">{formatProductName(p)}</h3>
                <div className="flex items-baseline gap-2 mt-1.5">
                  <p className="text-xs sm:text-sm text-[#8A8A8A] tracking-[0.1em]">${p.price?.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ─── 3. BRAND STATEMENT ──────────────────── */}
      <section data-testid="brand-statement-section" className="bg-[#0A0A0A] py-[72px] lg:py-[120px] px-6 text-center">
        <p className="font-['Inter'] text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">AERWEAR / After Hours Athletics</p>
        <h2
          data-testid="brand-statement-headline"
          className="mt-6 text-[40px] lg:text-[88px] text-[#F2F0EB]"
          style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontStretch: "125%", letterSpacing: "-0.01em", lineHeight: 0.95, textTransform: "uppercase" }}
        >
          <span className="block">Built For Motion.</span>
          <span className="block">Made For After Dark.</span>
        </h2>
      </section>

      {/* ─── 5. PRODUCT GRID ─────────────────────── */}
      {apparel.length > 0 && (
        <section data-testid="product-grid-section" className="py-16 sm:py-24 px-6 sm:px-12">
          <h2 className="text-xl sm:text-2xl font-medium uppercase tracking-[0.2em] text-[#F2F0EB] mb-10">All Apparel</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6 sm:gap-y-12">
            {apparel.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

    </div>
  );
}
